import {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useState,
} from "react";
import {
    getCurrentUser,
    LoginErrorCode,
    loginWithAPI,
    logoutWithAPI,
} from "../api/auth";
import { UIElement, User } from "../types";

interface AuthContextType {
    user: User | null | undefined;
    isAllowed: (uiElement: UIElement) => boolean;
    loading: boolean;
    error: string | null;
    fetchAndSetUser: () => Promise<void>;
    login: (
        email: string,
        password: string
    ) => Promise<{
        status: LOGIN_STATUS[number];
        error?: string;
        error_code?: LoginErrorCode;
    }>;
    logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export enum LOGIN_STATUS {
    SUCCESS = "success",
    OTP = "otp",
    ERROR = "error",
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
    const [user, setUser] = useState<User | null | undefined>();
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const fetchAndSetUser = async () => {
        try {
            const currentUserData = await getCurrentUser();
            if (!currentUserData) {
                setUser(null);
                setLoading(false);
                return;
            }
            setUser(currentUserData);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching user:", error);
            setLoading(false);
            setError("Failed to fetch user");
        }
    };

    useEffect(() => {
        fetchAndSetUser();
    }, []);

    const login: AuthContextType["login"] = async (
        email: string,
        password: string
    ) => {
        try {
            const loginResponse = await loginWithAPI({ email, password });

            if (!loginResponse || loginResponse.error) {
                return {
                    status: LOGIN_STATUS.ERROR,
                    error: loginResponse?.error,
                    error_code: loginResponse?.error_code,
                };
            }

            if (
                loginResponse.challenge === "EMAIL_OTP" ||
                loginResponse.challenge === "SMS_MFA"
            ) {
                return { status: LOGIN_STATUS.OTP };
            }

            await fetchAndSetUser();

            return { status: LOGIN_STATUS.SUCCESS };
        } catch (error) {
            console.error("Login error:", error);
            return {
                status: LOGIN_STATUS.ERROR,
                error:
                    error instanceof Error
                        ? error.message
                        : "An unexpected error occurred",
            };
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        try {
            const successfulLogout = await logoutWithAPI();
            if (!successfulLogout) {
                setError("Failed to logout");
                return;
            }
            setUser(null);
        } catch (error) {
            console.error("Logout error:", error);
        }
    };

    const isAllowed = (uiElement: UIElement): boolean => {
        return Boolean(user && !user.blockedUIElements.includes(uiElement));
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                isAllowed,
                loading,
                error,
                login,
                logout,
                fetchAndSetUser,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
